/**
 * Single source of truth for which merge-tag placeholders each
 * notification event supports. Lives next to `event-helpers.ts` so the
 * server renderers and Prisma-typed event helpers can import it
 * directly — the `shared/` package path was avoided because it did not
 * resolve reliably at server runtime.
 *
 * Consumed by:
 *   - server/src/Controllers/notification/schedule/notification-schedule-utils.ts
 *     — `createMessageFromTemplateNonRepeating` iterates
 *     `APPOINTMENT_TAG_KEYS` so its substitution list cannot drift.
 *   - server/src/Controllers/notification-setting/dispatcher/render-subscription-message.ts
 *     — `SUBSCRIPTION_TAG_KEYS` drives the placeholder-name set.
 *   - client/src/features/notification-settings/utils/mergeTags.ts
 *     — imports via a relative path; Vite bundles the module. The file
 *     has no server-only runtime deps (no Prisma, no env), so reaching
 *     into `server/src` from the client build is safe.
 *
 * Add a new tag here and every consumer picks it up; forget to wire a
 * resolver and the `Record<TagKey, string>` in the server renderer stops
 * type-checking. Labels and translations stay in the client.
 */

import type { NotificationSettingChannelType } from "../../../generated/prisma/client";

// Mirrors the server's Prisma `NotificationEvent` enum. Kept as a
// literal union so this file is importable by both client and server;
// server/src/Controllers/notification-setting/helpers/event-helpers.ts
// asserts the two stay in lockstep via a type equivalence check.
export type NotificationEventName =
  | "appointmentReminder"
  | "appointmentFollowUp"
  | "newAppointment"
  | "rescheduled"
  | "canceled"
  | "noShow"
  | "thankYouVisiting"
  | "rebookReminder"
  | "birthday"
  | "winBack"
  | "customerThreshold"
  | "subscriptionPeriodThreshold"
  | "subscriptionCreated"
  | "subscriptionPeriodEnding"
  | "subscriptionEnded"
  | "subscriptionEndedFollowUp"
  | "subscriptionLapsedFollowUp"
  | "subscriptionFreeze"
  | "subscriptionUnfreeze"
  | "subscriptionCanceled"
  | "subscriptionPaymentRequest"
  | "subscriptionPaymentReminder"
  | "subscriptionAutoCancelWarning"
  | "subscriptionAutoCanceled"
  | "subscriptionRenewed"
  | "subscriptionBillingDocument";

export type TagCategory =
  | "customer"
  | "appointment"
  | "location"
  | "business"
  | "subscription";

/**
 * Appointment-anchored tag keys. Match 1:1 with the `.replaceAll()` calls
 * in `createMessageFromTemplateNonRepeating`. The server's `Record<
 * AppointmentTagKey, string>` resolver map enforces that every key here
 * has a runtime value.
 *
 * Note: `{gsm}` exists on the server as a backwards-compatible alias for
 * `{locationPhone}`. It is intentionally NOT in this list so the UI
 * picker does not surface a duplicate.
 */
export const APPOINTMENT_TAG_KEYS = [
  "clientName",
  "clientLastName",
  "businessName",
  "day",
  "dayName",
  "year",
  "time",
  "serviceName",
  "appointmentPrice",
  "employeeName",
  "employeePhone",
  "tag",
  "rescheduleLink",
  "rescheduleFromDateTime",
  "locationName",
  "locationAddress",
  "locationPhone",
] as const;

/**
 * Subscription-event tag keys the renderer fills, offered or not, so a
 * template keeps rendering a tag its event does not offer (`getTagsForEvent`).
 *
 * `rescheduleLink` is intentionally shared with `APPOINTMENT_TAG_KEYS` — same
 * placeholder, same customer booking link — so it keeps its `appointment`
 * category in the picker.
 */
export const SUBSCRIPTION_TAG_KEYS = [
  "customerName",
  "customerLastName",
  "businessName",
  "subscriptionName",
  "rescheduleLink",
  "subscriptionPrice",
  "subscriptionStatus",
  "subscriptionBillingLink",
  "creditsRemaining",
  "visitsRemaining",
  "periodStart",
  "periodEnd",
  "amountDue",
  "dueDate",
  "daysOverdue",
  "cancellationDate",
  "unpaidSince",
  "documentNumber",
  "previousPeriodStart",
  "previousPeriodEnd",
  "currentPeriodStart",
  "currentPeriodEnd",
  "nextPeriodStart",
  "nextPeriodEnd",
  "nextPeriodAmount",
  "freezeStart",
  "freezeEnd",
] as const;

export type AppointmentTagKey = (typeof APPOINTMENT_TAG_KEYS)[number];
export type SubscriptionTagKey = (typeof SUBSCRIPTION_TAG_KEYS)[number];
export type TagKey = AppointmentTagKey | SubscriptionTagKey;

export type TagDefinition = {
  key: TagKey;
  placeholder: string; // always `{${key}}`
  category: TagCategory;
};

const TAG_CATEGORY: Record<TagKey, TagCategory> = {
  // customer (appointment-scope + subscription-scope keys — different names
  // intentionally; the server uses `clientName` for appointments and
  // `customerName` for subscriptions)
  clientName: "customer",
  clientLastName: "customer",
  customerName: "customer",
  customerLastName: "customer",
  // business
  businessName: "business",
  // appointment
  day: "appointment",
  dayName: "appointment",
  year: "appointment",
  time: "appointment",
  serviceName: "appointment",
  appointmentPrice: "appointment",
  employeeName: "appointment",
  employeePhone: "appointment",
  tag: "appointment",
  rescheduleLink: "appointment",
  rescheduleFromDateTime: "appointment",
  // location
  locationName: "location",
  locationAddress: "location",
  locationPhone: "location",
  // subscription
  subscriptionName: "subscription",
  subscriptionPrice: "subscription",
  subscriptionStatus: "subscription",
  subscriptionBillingLink: "subscription",
  creditsRemaining: "subscription",
  visitsRemaining: "subscription",
  periodStart: "subscription",
  periodEnd: "subscription",
  amountDue: "subscription",
  dueDate: "subscription",
  daysOverdue: "subscription",
  cancellationDate: "subscription",
  unpaidSince: "subscription",
  documentNumber: "subscription",
  previousPeriodStart: "subscription",
  previousPeriodEnd: "subscription",
  currentPeriodStart: "subscription",
  currentPeriodEnd: "subscription",
  nextPeriodStart: "subscription",
  nextPeriodEnd: "subscription",
  nextPeriodAmount: "subscription",
  freezeStart: "subscription",
  freezeEnd: "subscription",
};

function toDefinition(key: TagKey): TagDefinition {
  return { key, placeholder: `{${key}}`, category: TAG_CATEGORY[key] };
}

type SubscriptionEventName = Extract<
  NotificationEventName,
  `subscription${string}`
>;

const NAME_TAGS = [
  "customerName",
  "customerLastName",
  "businessName",
  "subscriptionName",
] as const;
const PERIOD_TAGS = ["periodStart", "periodEnd"] as const;
const BALANCE_TAGS = ["creditsRemaining", "visitsRemaining"] as const;

// Only the tags each message has a use for (docs/notifications-refactor.md); the renderer still fills every tag.
const SUBSCRIPTION_EVENT_TAGS: Record<
  SubscriptionEventName,
  readonly SubscriptionTagKey[]
> = {
  subscriptionCreated: [
    ...NAME_TAGS,
    "rescheduleLink",
    "subscriptionPrice",
    ...BALANCE_TAGS,
    ...PERIOD_TAGS,
    "documentNumber",
  ],
  subscriptionRenewed: [
    ...NAME_TAGS,
    "rescheduleLink",
    ...BALANCE_TAGS,
    ...PERIOD_TAGS,
    "amountDue",
    "documentNumber",
  ],
  subscriptionPeriodEnding: [
    ...NAME_TAGS,
    "rescheduleLink",
    "subscriptionPrice",
    ...BALANCE_TAGS,
    ...PERIOD_TAGS,
    "nextPeriodAmount",
  ],
  subscriptionLapsedFollowUp: [
    ...NAME_TAGS,
    "rescheduleLink",
    "subscriptionPrice",
    ...PERIOD_TAGS,
  ],
  subscriptionEnded: [
    ...NAME_TAGS,
    "rescheduleLink",
    "subscriptionPrice",
    ...PERIOD_TAGS,
  ],
  subscriptionEndedFollowUp: [
    ...NAME_TAGS,
    "rescheduleLink",
    "subscriptionPrice",
    ...PERIOD_TAGS,
  ],
  subscriptionPeriodThreshold: [...NAME_TAGS, "rescheduleLink"],
  subscriptionFreeze: [
    ...NAME_TAGS,
    ...BALANCE_TAGS,
    "freezeStart",
    "freezeEnd",
  ],
  subscriptionUnfreeze: [
    ...NAME_TAGS,
    "rescheduleLink",
    ...BALANCE_TAGS,
    ...PERIOD_TAGS,
    "freezeStart",
    "freezeEnd",
  ],
  subscriptionCanceled: [...NAME_TAGS, ...PERIOD_TAGS],
  subscriptionPaymentRequest: [
    ...NAME_TAGS,
    ...PERIOD_TAGS,
    "amountDue",
    "dueDate",
    "documentNumber",
  ],
  subscriptionPaymentReminder: [
    ...NAME_TAGS,
    ...PERIOD_TAGS,
    "amountDue",
    "dueDate",
    "daysOverdue",
    "documentNumber",
  ],
  subscriptionAutoCancelWarning: [
    ...NAME_TAGS,
    ...PERIOD_TAGS,
    "amountDue",
    "dueDate",
    "daysOverdue",
    "cancellationDate",
    "documentNumber",
  ],
  subscriptionAutoCanceled: [
    ...NAME_TAGS,
    ...PERIOD_TAGS,
    "amountDue",
    "unpaidSince",
    "documentNumber",
  ],
  subscriptionBillingDocument: [
    ...NAME_TAGS,
    ...PERIOD_TAGS,
    "amountDue",
    "documentNumber",
  ],
};

export function isSubscriptionEvent(
  event: NotificationEventName,
): event is SubscriptionEventName {
  return Object.hasOwn(SUBSCRIPTION_EVENT_TAGS, event);
}

// The only dispatches that say which billing period the message is about.
const PERIOD_ANCHORED_EVENTS = new Set<NotificationEventName>([
  "subscriptionCreated",
  "subscriptionPeriodEnding",
  "subscriptionLapsedFollowUp",
  "subscriptionEndedFollowUp",
  "subscriptionPaymentRequest",
  "subscriptionPaymentReminder",
  "subscriptionAutoCancelWarning",
  "subscriptionAutoCanceled",
  "subscriptionRenewed",
  "subscriptionBillingDocument",
]);

export function isPeriodAnchoredEvent(event: NotificationEventName): boolean {
  return PERIOD_ANCHORED_EVENTS.has(event);
}

// Events whose message is about a period the member has not paid yet; the
// other period-anchored events are about the last paid period.
const UNPAID_PERIOD_EVENTS = new Set<NotificationEventName>([
  "subscriptionPaymentRequest",
  "subscriptionPaymentReminder",
  "subscriptionAutoCancelWarning",
]);

export function isUnpaidPeriodEvent(event: NotificationEventName): boolean {
  return UNPAID_PERIOD_EVENTS.has(event);
}

export type TagOptions = {
  /** False for clients with `preventMarketplaceSync`: no marketplace link is offered. */
  showsMarketplaceLinks: boolean;
  /** The channel the template is written for; without one, channel-bound tags are left out. */
  channel?: NotificationSettingChannelType;
};

// Infobip shortens every `https://` and `www.` link in a phone message, and on
// iOS a shortened link opens the website instead of the app. Viber and
// WhatsApp therefore carry the link without its scheme. SMS carries none: the
// bare link is ~300 characters (two segments), and an SMS app may not show it
// as a link.
export function billingLinkReachesChannel(
  channel: NotificationSettingChannelType,
): boolean {
  return channel !== "sms";
}

/** `{subscriptionBillingLink}`'s value in a body for `channel`, from the full `https://` link. */
export function formatBillingLinkForChannel(
  link: string,
  channel: NotificationSettingChannelType,
): string {
  if (!billingLinkReachesChannel(channel)) return "";
  return channel === "email" ? link : link.replace(/^https:\/\//, "");
}

function offersBillingLink(options: TagOptions): boolean {
  return (
    options.showsMarketplaceLinks &&
    options.channel !== undefined &&
    billingLinkReachesChannel(options.channel)
  );
}

/** The tags a template author can use for an event, in the picker's order. */
export function getTagsForEvent(
  event: NotificationEventName,
  options: TagOptions,
): readonly TagDefinition[] {
  if (!isSubscriptionEvent(event)) {
    return APPOINTMENT_TAG_KEYS.map(toDefinition);
  }
  const offered: readonly SubscriptionTagKey[] = offersBillingLink(options)
    ? [...SUBSCRIPTION_EVENT_TAGS[event], "subscriptionBillingLink"]
    : SUBSCRIPTION_EVENT_TAGS[event];
  return SUBSCRIPTION_TAG_KEYS.filter((key) => offered.includes(key)).map(
    toDefinition,
  );
}
